/**
 * Política de senha (D-017, baseada no NIST SP 800-63B):
 *  - mínimo de 12 caracteres (frases longas são bem-vindas; máximo de 128);
 *  - recusa senhas comuns/vazadas e senhas que contenham o nome ou o e-mail;
 *  - NÃO exige "1 maiúscula + 1 símbolo" nem troca periódica (pioram a segurança na prática).
 */
import { z } from 'zod';

export const SENHA_MINIMO = 12;
export const SENHA_MAXIMO = 128;

/**
 * Lista local de senhas e trechos mais comuns em vazamentos, mais termos do contexto.
 * Comparação sem acento e sem diferenciar maiúsculas; também recusa a senha se ela
 * for só um destes trechos repetido ou seguido de números.
 */
const TRECHOS_PROIBIDOS = [
  'senha',
  'password',
  'passw0rd',
  'qwerty',
  'asdfgh',
  'zxcvbn',
  'abc123',
  'abcdef',
  'iloveyou',
  'admin',
  'administrador',
  'welcome',
  'bemvindo',
  'brasil',
  'mudar',
  'mudarsenha',
  'trocar',
  'metaepi',
  'sifilis',
  'saude',
  'sus',
  'ubs',
  'hospital',
  'maternidade',
  'enfermagem',
  'medico',
  'prefeitura',
  'secretaria',
  'teste',
  'usuario',
  'login',
  'acesso',
  'master',
  'dragon',
  'monkey',
  'letmein',
  'football',
  'futebol',
  'flamengo',
  'corinthians',
  'palmeiras',
  'santos',
  'jesus',
  'deus',
];

const SEQUENCIAS = ['0123456789', '1234567890', '9876543210', 'abcdefghij', 'qwertyuiop'];

function normalizar(texto: string): string {
  // \p{M} = marcas de acento separadas pelo NFD (á → a + ´)
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

export interface ContextoSenha {
  nome?: string;
  email?: string;
}

/** Devolve a lista de problemas da senha (vazia = senha aceita). Mensagens prontas para a tela. */
export function problemasSenha(senha: string, contexto: ContextoSenha = {}): string[] {
  const problemas: string[] = [];
  if (senha.length < SENHA_MINIMO) problemas.push(`Use pelo menos ${SENHA_MINIMO} caracteres.`);
  if (senha.length > SENHA_MAXIMO) problemas.push(`Use no máximo ${SENHA_MAXIMO} caracteres.`);

  const s = normalizar(senha);
  const semRepeticoes = s.replace(/(.)\1+/g, '$1');
  if (new Set(s).size <= 3) problemas.push('Evite repetir os mesmos caracteres.');

  const nucleo = s.replace(/[^a-z]/g, '');
  if (
    TRECHOS_PROIBIDOS.some(
      (trecho) =>
        nucleo === trecho || (nucleo.length <= trecho.length + 2 && nucleo.includes(trecho)),
    ) ||
    SEQUENCIAS.some((seq) => seq.includes(semRepeticoes) || semRepeticoes.includes(seq.slice(0, 8)))
  ) {
    problemas.push('Esta senha é muito comum. Prefira uma frase que só você conheça.');
  }

  const partesNome = normalizar(contexto.nome ?? '')
    .split(/\s+/)
    .filter((parte) => parte.length >= 4);
  const usuarioEmail = normalizar(contexto.email ?? '').split('@')[0] ?? '';
  if (
    partesNome.some((parte) => s.includes(parte)) ||
    (usuarioEmail.length >= 4 && s.includes(usuarioEmail))
  ) {
    problemas.push('A senha não pode conter seu nome ou seu e-mail.');
  }
  return problemas;
}

/** Schema simples (sem contexto) para formulários; o contexto é conferido na API. */
export const novaSenhaSchema = z.string().superRefine((senha, ctx) => {
  for (const problema of problemasSenha(senha)) ctx.addIssue({ code: 'custom', message: problema });
});
